import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Trash2, ShieldCheck, AlertTriangle } from "lucide-react";

const LABELS = {
  empresas: "Empresas",
  montadoras: "Montadoras",
  montadores: "Montadores",
  usuarios: "Usuários (exceto ADM)",
  servicos: "Serviços",
  leads_particulares_nao_pagos: "Clientes particulares NÃO pagos",
  outros_registros_vinculados: "Outros registros vinculados",
};

export default function LimpezaTeste() {
  const [previa, setPrevia] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [executando, setExecutando] = useState(false);
  const [resultado, setResultado] = useState(null);

  const loadPrevia = () => api.get("/admin/limpeza-teste/previa")
    .then(r => setPrevia(r.data)).catch(e => toast.error(formatApiError(e)));
  useEffect(() => { loadPrevia(); }, []);

  const executar = async () => {
    setExecutando(true);
    try {
      const r = await api.post("/admin/limpeza-teste/executar", { confirmacao: confirm });
      setResultado(r.data);
      setConfirm("");
      toast.success(`Limpeza concluída: ${r.data.total_apagados} registros apagados`);
      loadPrevia();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setExecutando(false); }
  };

  const preservados = previa?.preservar?.clientes_particulares_pagos || [];

  return (
    <div className="p-8 max-w-3xl">
      <div className="label-cap">Sistema</div>
      <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1 mb-2">Limpeza de Dados de Teste</h1>
      <p className="text-sm text-muted-foreground mb-6">
        Apaga cadastros e dados operacionais de teste (empresas, montadoras, montadores, usuários e serviços),
        preservando os clientes particulares com pagamento confirmado, todas as configurações e o funcionamento da plataforma.
      </p>

      {!previa ? (
        <div className="text-muted-foreground text-sm">Carregando prévia…</div>
      ) : (
        <>
          <div className="border border-border bg-white p-5 mb-4" data-testid="limpeza-preservar">
            <div className="flex items-center gap-2 mb-3 text-[#166534] font-bold text-sm uppercase tracking-wider">
              <ShieldCheck size={16} /> Será preservado
            </div>
            {preservados.length === 0 ? (
              <div className="text-sm text-muted-foreground">Nenhum cliente particular pago encontrado neste banco.</div>
            ) : (
              <ul className="space-y-2">
                {preservados.map((c, i) => (
                  <li key={i} className="text-sm flex flex-wrap gap-2 items-center" data-testid={`preservado-${i}`}>
                    <span className="px-2 py-0.5 bg-[#DCFCE7] text-[#166534] text-[10px] font-bold uppercase">PAGO</span>
                    <strong>{c.nome || "—"}</strong>
                    <span className="text-muted-foreground font-mono text-xs">{c.protocolo}</span>
                    <span className="text-xs text-muted-foreground">status: {c.status}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="text-xs text-muted-foreground mt-3">
              + {previa.preservar.servicos_vinculados} serviço(s) vinculado(s), configurações, catálogos, caches e documentos da plataforma.
            </div>
          </div>

          <div className="border border-border border-l-4 border-l-[#E60000] bg-white p-5 mb-6" data-testid="limpeza-apagar">
            <div className="flex items-center gap-2 mb-3 text-[#B91C1C] font-bold text-sm uppercase tracking-wider">
              <Trash2 size={16} /> Será apagado
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(previa.apagar).map(([k, v]) => (
                <div key={k} className="border border-border p-3" data-testid={`apagar-${k}`}>
                  <div className="text-2xl font-extrabold">{v}</div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{LABELS[k] || k}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="border-2 border-[#E60000] bg-[#FEF2F2] p-5" data-testid="limpeza-confirmacao">
            <div className="flex items-center gap-2 mb-2 text-[#B91C1C] font-bold text-sm">
              <AlertTriangle size={16} /> Esta ação é irreversível neste ambiente
            </div>
            <p className="text-xs text-muted-foreground mb-3">Digite <strong>LIMPAR</strong> para confirmar:</p>
            <div className="flex gap-3">
              <Input value={confirm} onChange={(e) => setConfirm(e.target.value)}
                placeholder="LIMPAR" className="w-48 bg-white" data-testid="limpeza-input" />
              <Button onClick={executar} disabled={confirm.trim().toUpperCase() !== "LIMPAR" || executando}
                className="btn-sharp bg-[#E60000] text-white hover:bg-[#B91C1C]" data-testid="limpeza-executar">
                {executando ? "Limpando…" : "Executar limpeza"}
              </Button>
            </div>
          </div>

          {resultado && (
            <div className="border border-border bg-white p-5 mt-6" data-testid="limpeza-resultado">
              <div className="font-bold text-sm mb-2">✅ Limpeza concluída — {resultado.total_apagados} registros apagados</div>
              <div className="text-xs text-muted-foreground mb-2">
                Preservados: {resultado.preservados.clientes_particulares_pagos} cliente(s) particular(es) pago(s) e {resultado.preservados.servicos_vinculados} serviço(s).
              </div>
              <div className="text-xs font-mono max-h-48 overflow-y-auto">
                {Object.entries(resultado.apagados_por_colecao || {}).map(([c, n]) => (
                  <div key={c}>{c}: {n}</div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
